import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {
  type RepoIssuesDashboardResult,
  RepoIssuesDashboardContentResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'

import {createRowNodeLoader} from './issues-dashboard-row-node'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'

export type RepoRowItem = ReturnType<typeof repoIssuesToListItems>[number]

// The `_rows` response reuses the S0 content proto (results only), rendered
// under this ReactPayload data key. Proto-mode responses carry X-Proto-JSON, so
// the body is proto3-JSON that we decode with fromJson.
const ROWS_PAYLOAD_KEY = 'issuesIndexRowsData'

type RowsEnvelope = {payload?: {issuesIndexRowsData?: unknown}}

/**
 * Fetch fresh main rows for the repository Issues index from
 * `GET /:owner/:repo/issues/_rows`, keyed by each row's global identity so the
 * batching loader can resolve individual requests.
 */
export async function fetchRepoRowNodes(
  owner: string,
  repo: string,
  globalIds: string[],
): Promise<Map<string, RepoRowItem>> {
  const json = await queryFnFetch<RowsEnvelope>({
    queryDeps: {
      pathname: `/${owner}/${repo}/issues/_rows`,
      searchParams: {ids: globalIds.join(',')},
    },
  })
  const raw = json.payload?.[ROWS_PAYLOAD_KEY]
  const results: RepoIssuesDashboardResult[] = raw
    ? fromJson({
        schema: RepoIssuesDashboardContentResponseSchema,
        raw,
        routeId: ROWS_PAYLOAD_KEY,
        queryName: 'repoIssuesRows',
      }).results
    : []

  const byIdentity = new Map<string, RepoRowItem>()
  for (const item of repoIssuesToListItems(results)) {
    byIdentity.set(getListItemIdentity(item), item)
  }
  return byIdentity
}

// The repository Issues index row-node loader, over GET /:owner/:repo/issues/_rows.
// Built per page (owner/repo are stable there) so it reuses the shared batching
// queue without sharing pending state with the dashboard loader.
export function createRepoRowNodeLoader(
  owner: string,
  repo: string,
): {
  loadRowNode: (globalId: string) => Promise<RepoRowItem | undefined>
} {
  return createRowNodeLoader<RepoRowItem>(globalIds => fetchRepoRowNodes(owner, repo, globalIds))
}
